<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import Button from "primevue/button";
import Menu from "primevue/menu";
import { DirectLink, Participant, Project, Trainer } from "../../../types/PersologOnlineTypes";
import { directLinkRecipients, sendBlankEmailToAll, sendDirectLinkToAll } from "../../../utils/EmailUtils";

const { t } = useI18n();

const props = defineProps({
    participants: {
        type: Array as () => Participant[],
        required: true,
    },
    project: {
        type: Object as () => Project,
        required: true,
    },
    trainer: {
        type: Object as () => Trainer,
        required: true,
    },
    directLink: {
        type: Object as () => DirectLink | null,
        default: null,
    },
});

// Only direct-link projects can offer a general "resend the link to everyone" mail;
// a normal project's links are per-participant and can't be composed for all at once.
const isDirectLink = computed(() => props.directLink != null);
const hasDirectLinkRecipients = computed(() => directLinkRecipients(props.participants).length > 0);

const menu = ref();
const toggle = (event: unknown) => {
    menu.value.toggle(event);
};

const emailEveryone = () => sendBlankEmailToAll(props.trainer, props.participants, props.project);
const sendLinkToAll = () =>
    sendDirectLinkToAll(props.trainer, props.participants, props.project, props.directLink);

const items = computed(() => [
    {
        label: t("project-details.participants.email-all.email-everyone"),
        icon: "pi pi-envelope",
        command: emailEveryone,
    },
    {
        label: t("project-details.participants.email-all.send-direct-link"),
        icon: "pi pi-link",
        disabled: !hasDirectLinkRecipients.value,
        command: sendLinkToAll,
    },
]);
</script>

<template>
    <!-- Direct-link projects get a dropdown (contact everyone / resend the direct link);
         other projects keep the single blank-email action they had before. -->
    <template v-if="isDirectLink">
        <Button
            :id="`email-all-button_btn_${project.id}`"
            outlined
            icon="pi pi-envelope"
            type="button"
            aria-haspopup="true"
            :aria-controls="`email-all-button_menu_${project.id}`"
            v-tooltip.top="{ value: t('project-details.participants.tooltips.email'), showDelay: 500 }"
            :aria-label="t('project-details.participants.tooltips.email')"
            @click="toggle"
        />
        <Menu :id="`email-all-button_menu_${project.id}`" ref="menu" :model="items" popup />
    </template>
    <Button
        v-else
        :id="`email-all-button_btn_${project.id}`"
        outlined
        icon="pi pi-envelope"
        type="button"
        v-tooltip.top="{ value: t('project-details.participants.tooltips.email'), showDelay: 500 }"
        :aria-label="t('project-details.participants.tooltips.email')"
        @click="emailEveryone"
    />
</template>
